/**
 * Admin area — DESIGN.md §9.2, §13.1.
 *
 * `/admin/{users,groups,lookups,fields,views,automations,email}`. The guard is
 * here so every child inherits it; the tabs for surfaces that arrive later are
 * shown disabled with their milestone, which doubles as a build progress map.
 */
import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth';
import { Page } from '@/components/page';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

const TABS: { to: string; label: string; milestone?: string }[] = [
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/groups', label: 'Groups' },
  { to: '/admin/lookups', label: 'Lookups' },
  { to: '/admin/views', label: 'Shared views', milestone: 'M3' },
  { to: '/admin/automations', label: 'Automations' },
  { to: '/admin/tokens', label: 'Tokens' },
  { to: '/admin/audit', label: 'Audit log' },
  { to: '/admin/export', label: 'Export' },
];

function AdminLayout(): JSX.Element {
  const { isAdmin } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (!isAdmin) {
    return (
      <Page title="Admin">
        <EmptyState
          title="Administrators only"
          description="User management, lookups, and automations are restricted to administrators. Ask Brad if you need access."
        />
      </Page>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl p-4 md:p-6">
      <h1 className="text-xl font-semibold tracking-tight">Admin</h1>
      {/*
        The rule lives on the wrapper, and the scrolling strip is pulled a
        pixel over it. Putting both on one element meant the active tab's
        `-mb-px` underline sat one pixel below the scroll container's content
        box, which `overflow-x-auto` answered with a vertical scrollbar — a
        scrollbar for one pixel nobody can scroll to.
      */}
      <div className="mt-4 border-b">
        <nav
          aria-label="Admin sections"
          className="scrollbar-thin -mb-px flex gap-1 overflow-x-auto overflow-y-hidden"
        >
          {TABS.map((tab) => {
            const active = pathname.startsWith(tab.to);
            const disabled = Boolean(tab.milestone);
            return disabled ? (
              <span
                key={tab.to}
                className="text-muted-foreground/60 flex cursor-not-allowed items-center gap-1.5 whitespace-nowrap px-3 py-2 text-sm"
                title={`Arrives in milestone ${tab.milestone}`}
              >
                {tab.label}
                <span className="bg-muted rounded px-1.5 py-0.5 text-[0.65rem] font-medium">
                  {tab.milestone}
                </span>
              </span>
            ) : (
              <Link
                key={tab.to}
                to={tab.to}
                className={cn(
                  'whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
                  active
                    ? 'border-primary text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground border-transparent',
                )}
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="mt-5">
        <Outlet />
      </div>
    </div>
  );
}
